import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectFeatureFlagAgentCart } from '../../redux/features/config/config.selector';
import { selectIsCarPresent } from '../../selectors/message';
import useAISuggestionActions from '../AISuggestions/useAISuggestionActions.hooks';
import useEventListener from '../../hooks/useEventListener.hooks';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';

/**
 * PULSE ADVISE (HITL 3.0) — the AI co-pilot's recommended ask, folded into the
 * Pulse strip from the old Action Center: the suggestion text with Accept /
 * Dismiss (tick / cross) on one row.
 *
 * Gated by the agent_cart flag (same master switch as the standalone suggestion
 * surface). Accept / Dismiss and the Alt+A / Alt+D shortcuts drive the shared
 * useAISuggestionActions path. Deliberately omits the old "AI recommends you
 * ask:" label and the reason line; the slash-command reminder now lives in
 * PulseSignals. Renders nothing when there's no active suggestion.
 */
const useStyles = makeStyles((theme: Theme) => ({
  ask: {
    // 8px inset on all sides; the bottom margin also keeps the ask off the
    // pinned AI status band at the bottom of the Pulse card.
    marginTop: HITL3_PADDING,
    marginBottom: HITL3_PADDING,
    marginLeft: HITL3_PADDING,
    marginRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: '#eef1fb',
    border: '1px solid #c5cae9',
    borderRadius: '8px',
    padding: theme.spacing(0.875, 1.125),
  },
  // Match the "Welcome" intent pill's text: medium weight, shared hitl3 size.
  q: {
    flex: 1,
    minWidth: 0,
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    color: Colors.readBack,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  okBtn: {
    backgroundColor: Colors.green,
    color: Colors.white,
    '&:hover': { backgroundColor: Colors.green },
  },
  noBtn: {
    backgroundColor: Colors.white,
    color: '#79808a',
    border: `1px solid ${Colors.alto}`,
  },
}));

const PulseAdvise = () => {
  const classes = useStyles();

  // The co-pilot advise loop is gated by the agent_cart flag, matching the
  // standalone AISuggestions surface.
  const isAgentCartEnabled = useSelector(selectFeatureFlagAgentCart);
  // HITL-1534: a suggestion is something to SAY to the customer at the menu
  // board, so once the car has pulled away it is dead by definition — accepting
  // it would speak the departed customer's line (their total) at whoever is at
  // the board next. The slice reset on car_enter handles the next session; this
  // covers the window between car_exit and that car_enter.
  //
  // Deliberately a DISPLAY gate rather than a car_exit reset of the slice:
  // clearing AI state on car_exit caused a prod incident (dropped order + cart
  // revert) because a car can leave the board while the same session's order is
  // still being finished — see the note at CustomerPulse/index.tsx. Hiding the
  // card destroys nothing, so a session that outlives the car keeps its state.
  const isCarPresent = useSelector(selectIsCarPresent);
  const { activeSuggestion, accept, dismiss } = useAISuggestionActions();
  const showAsk = isAgentCartEnabled && isCarPresent && !!activeSuggestion;

  // Alt+A / Alt+D mirror the Accept / Dismiss buttons (shared path via
  // useAISuggestionActions). `event.code` is matched (not `event.key`) so macOS
  // Option-key glyphs don't break the match; capture phase so it fires even
  // while the omnibar is focused. No-ops unless the co-pilot is enabled and a
  // suggestion is showing.
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyA' && e.code !== 'KeyD') {
      return;
    }
    // Gated on `showAsk`, not just the flag + suggestion: the shortcut must not
    // reach a card the agent cannot see. Without the car-presence check here the
    // hidden post-car_exit suggestion would still be acceptable by keyboard
    // (HITL-1534), which is the harmful half of the bug.
    if (!showAsk || !activeSuggestion) {
      return;
    }
    e.preventDefault();
    // stopPropagation, unlike Alt+1…9 in useIntentHotkeys, which deliberately lets
    // the take-over guard see the press first. Not an inconsistency: the rule is not
    // "speaking to the customer prompts", it is "acting on your OWN initiative
    // prompts". Accepting a co-pilot card ratifies the AI's own line — it goes out
    // stamped `intent: 'ai_suggestion'` and the AI keeps driving — where Alt+1 is an
    // utterance the agent chose, which is a takeover. Prompting here would ask the
    // agent to confirm taking over from the AI in order to agree with it.
    e.stopPropagation();
    if (e.code === 'KeyA') {
      accept(activeSuggestion);
    } else {
      dismiss(activeSuggestion);
    }
  };
  useEventListener('keydown', handleKeyDown, window, true);

  if (!showAsk || !activeSuggestion) {
    return null;
  }

  return (
    <div className={classes.ask} data-testid="pulse-advise">
      <span className={classes.q}>{activeSuggestion.title}</span>
      <div className={classes.actions}>
        <IconButton
          className={classes.okBtn}
          size="small"
          title="Accept (Alt+A)"
          aria-label="Accept suggestion"
          onClick={() => accept(activeSuggestion)}
          sx={{ p: 0.5 }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          className={classes.noBtn}
          size="small"
          title="Dismiss (Alt+D)"
          aria-label="Dismiss suggestion"
          onClick={() => dismiss(activeSuggestion)}
          sx={{ p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
};

export default PulseAdvise;
